import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Banknote, Box, CalendarDays, Check, ChevronDown, ChevronRight,
  CircleUserRound, Copy, CreditCard, Expand, Eye, EyeOff, Headphones,
  LayoutDashboard, LockKeyhole, LogOut, Menu, MessageCircle, Minus, PackageCheck, Pencil, Plus, Search,
  QrCode, RefreshCw, ShieldCheck, ShoppingBag, ShoppingCart, Sparkles, Trophy, Truck, UserRound, X, Zap
} from "lucide-react";
import { api, apiUpload } from "./api";
import {
  CartItem, maskCep, maskCpf, maskPhone, money, onlyDigits, Product, Storefront, User
} from "./lib";

type Page = "home" | "product" | "auth" | "cart" | "checkout" | "account" | "admin";
type Notice = { kind: "success" | "error"; text: string } | null;
type Order = {
  id: string; order_number: string; total_cents: number; status: string; payment_status: string;
  payment_method: string; items_json: string; tracking_code?: string; created_at: string;
};
type BillingInvoice = {
  id: string; amountCents: number; dueDate: string; paidAt: string; status: string;
  billingType: string; invoiceUrl: string;
};
type BillingCharge = BillingInvoice & {
  payload: string; encodedImage: string; expirationDate: string;
};
type BillingData = {
  provider: string;
  providerConfigured: boolean;
  monthlyAmountCents: number;
  client: {
    contactName: string; email: string; phone: string; documentMasked: string;
    hasDocument: boolean; billingDay: number; status: string;
  };
  subscription: {
    id: string; active: boolean; firstDueDate: string; billingDay: number;
  };
  currentCharge: BillingCharge | null;
  invoices: BillingInvoice[];
};
type ProductDraft = {
  id: string | null;
  name: string;
  subtitle: string;
  description: string;
  price: string;
  active: boolean;
  personalization_enabled: boolean;
  personalization_price: string;
  inventory: Record<string, number>;
  front_image: string;
  back_image: string;
};

const pageFromPath = (): Page => {
  const path = location.pathname;
  if (path.startsWith("/produto")) return "product";
  if (path.startsWith("/entrar") || path.startsWith("/cadastro")) return "auth";
  if (path.startsWith("/carrinho")) return "cart";
  if (path.startsWith("/checkout")) return "checkout";
  if (path.startsWith("/minha-conta")) return "account";
  if (path.startsWith("/admin")) return "admin";
  return "home";
};

const pagePath: Record<Page, string> = {
  home: "/", product: "/produto/camisa-oficial-sgz", auth: "/entrar", cart: "/carrinho",
  checkout: "/checkout", account: "/minha-conta", admin: "/admin"
};

const statusLabel: Record<string, string> = {
  new: "Novo", confirmed: "Confirmado", production: "Em produção", shipped: "Enviado",
  delivered: "Entregue", cancelled: "Cancelado", pending: "Pendente", paid: "Pago",
  refunded: "Estornado"
};

const GRAPHICS_SHOP_URL = "https://subliart10.lojavirtualnuvem.com.br/produtos/camisa-oficial-sgz-esports-wr/";
const CHAMPIONSHIP_WHATSAPP_URL = `https://wa.me/5527998064179?text=${encodeURIComponent(
  "Olá! Quero mais informações sobre a Copa Brasileira Wild Rift Season 2."
)}`;

const productDraft = (product?: Product): ProductDraft => ({
  id: product?.id || null,
  name: product?.name || "",
  subtitle: product?.subtitle || "",
  description: product?.description || "",
  price: product ? (product.price_cents / 100).toFixed(2).replace(".", ",") : "",
  active: product ? !!product.active : true,
  personalization_enabled: product ? !!product.personalization_enabled : true,
  personalization_price: product
    ? (product.personalization_price_cents / 100).toFixed(2).replace(".", ",")
    : "0,00",
  inventory: Object.fromEntries(
    ["P", "M", "G", "GG", "XG"].map((size) => [
      size,
      product?.inventory.find((entry) => entry.size === size)?.stock || 0
    ])
  ),
  front_image: product?.front_image || "/images/logo-sgz.png",
  back_image: product?.back_image || "/images/logo-sgz.png"
});

async function optimizeProductImage(file: File) {
  if (!file.type.startsWith("image/")) throw new Error("Escolha um arquivo de imagem válido.");
  const bitmap = await createImageBitmap(file);
  try {
    let maxDimension = 1600;
    let quality = 0.86;
    for (let attempt = 0; attempt < 3; attempt++) {
      const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Não foi possível preparar a imagem.");
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", quality));
      if (blob && blob.size <= 1_572_864) return blob;
      maxDimension -= 300;
      quality -= 0.1;
    }
  } finally {
    bitmap.close();
  }
  throw new Error("A imagem ficou muito grande. Use uma foto com menos resolução.");
}

export default function App() {
  const [page, setPage] = useState<Page>(pageFromPath);
  const [store, setStore] = useState<Storefront | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => {
    try { return JSON.parse(localStorage.getItem("sgz-cart") || "[]") as CartItem[]; } catch { return []; }
  });
  const [notice, setNotice] = useState<Notice>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api<Storefront>("/storefront"),
      api<{ user: User | null }>("/auth/me").catch(() => ({ user: null }))
    ]).then(([storeData, auth]) => { setStore(storeData); setUser(auth.user); })
      .catch((error: Error) => setNotice({ kind: "error", text: error.message }));
    const pop = () => setPage(pageFromPath());
    addEventListener("popstate", pop);
    return () => removeEventListener("popstate", pop);
  }, []);

  useEffect(() => localStorage.setItem("sgz-cart", JSON.stringify(cart)), [cart]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4200);
    return () => clearTimeout(timer);
  }, [notice]);

  const navigate = (next: Page) => {
    history.pushState({}, "", pagePath[next]);
    setPage(next); setMenuOpen(false); scrollTo({ top: 0, behavior: "smooth" });
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartSubtotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const addItem = (item: CartItem, goToCart = false) => {
    setCart((current) => {
      const index = current.findIndex((entry) =>
        entry.productId === item.productId && entry.size === item.size && entry.nickname === item.nickname
      );
      if (index < 0) return [...current, item];
      return current.map((entry, position) =>
        position === index ? { ...entry, quantity: entry.quantity + item.quantity } : entry
      );
    });
    setNotice({ kind: "success", text: "Camisa adicionada à sacola." });
    if (goToCart) navigate("cart");
  };

  if (!store) return <Loading />;
  const products = store.products?.length ? store.products : [store.product];
  const requestedSlug = decodeURIComponent(location.pathname.split("/").filter(Boolean).at(-1) || "");
  const selectedProduct = products.find((product) => product.slug === requestedSlug) || store.product;
  const openProduct = (product: Product) => {
    history.pushState({}, "", `/produto/${product.slug}`);
    setPage("product"); setMenuOpen(false); scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="app-shell">
      <Announcement text={store.settings.announcement} />
      <Header
        page={page} user={user} cartCount={cartCount} menuOpen={menuOpen}
        onMenu={() => setMenuOpen(!menuOpen)} navigate={navigate}
      />
      <main>
        {page === "home" && <Home products={products} navigate={navigate} openProduct={openProduct} buyHref={GRAPHICS_SHOP_URL} />}
        {page === "product" && <ProductPage key={selectedProduct.id} product={selectedProduct} buyHref={GRAPHICS_SHOP_URL} />}
        {page === "auth" && <AuthPage onAuthenticated={(account) => {
          setUser(account); navigate(cart.length ? "checkout" : "account");
        }} />}
        {page === "cart" && <CartPage cart={cart} subtotal={cartSubtotal} navigate={navigate} setCart={setCart} />}
        {page === "checkout" && (
          <CheckoutPage
            cart={cart} subtotal={cartSubtotal} user={user} settings={store.settings} navigate={navigate}
            onComplete={() => setCart([])}
          />
        )}
        {page === "account" && (
          user ? <AccountPage user={user} setUser={setUser} navigate={navigate} /> : <AuthGate navigate={navigate} />
        )}
        {page === "admin" && (
          user?.role === "admin" ? <AdminPage store={store} refreshStore={() =>
            api<Storefront>("/storefront").then(setStore)
          } /> : <AuthGate navigate={navigate} admin />
        )}
      </main>
      <Footer navigate={navigate} settings={store.settings} />
      {notice && <div className={`toast ${notice.kind}`}><Check size={18} />{notice.text}</div>}
    </div>
  );
}

function Loading() {
  return (
    <div className="loading-screen">
      <img src="/images/logo-sgz.png" alt="SGZ" />
      <div className="loading-line"><span /></div>
      <p>Preparando a experiência SGZ</p>
    </div>
  );
}

function Announcement({ text }: { text: string }) {
  return (
    <div className="announcement">
      <Sparkles size={13} /><span>{text || "ENVIO PARA TODO O BRASIL"}</span><Sparkles size={13} />
    </div>
  );
}

function Header({ page, user, cartCount, menuOpen, onMenu, navigate }: {
  page: Page; user: User | null; cartCount: number; menuOpen: boolean; onMenu: () => void;
  navigate: (page: Page) => void;
}) {
  const goToSection = (id: string) => {
    if (menuOpen) onMenu();
    if (page !== "home") navigate("home");
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
  };
  return (
    <header className="site-header">
      <div className="container header-inner">
        <button className="icon-button mobile-only" onClick={onMenu} aria-label="Abrir menu">
          {menuOpen ? <X /> : <Menu />}
        </button>
        <button className="brand" onClick={() => navigate("home")} aria-label="Página inicial SGZ">
          <img src="/images/logo-sgz.png" alt="SGZ" />
          <span><b>SGZ</b> STORE</span>
        </button>
        <nav className={menuOpen ? "main-nav open" : "main-nav"}>
          <button className={page === "home" ? "active" : ""} onClick={() => navigate("home")}>Início</button>
          <button className={page === "product" ? "active" : ""} onClick={() => navigate("product")}>Uniforme oficial</button>
          <button onClick={() => goToSection("equipe")}>Equipe</button>
          <button onClick={() => goToSection("story")}>Nossa identidade</button>
          {user?.role === "admin" && <button onClick={() => navigate("admin")}>Painel administrativo</button>}
        </nav>
        <div className="header-actions">
          <button className="icon-button hide-small" aria-label="Buscar"><Search /></button>
          <button className="account-link" onClick={() => navigate(user ? "account" : "auth")}>
            <CircleUserRound /><span>{user ? user.full_name.split(" ")[0] : "Entrar"}</span>
          </button>
          <button className="bag-button" onClick={() => navigate("cart")} aria-label={`Sacola com ${cartCount} itens`}>
            <ShoppingBag /><span>{cartCount}</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function Home({ products, navigate, openProduct, buyHref }: {
  products: Product[];
  navigate: (page: Page) => void;
  openProduct: (product: Product) => void;
  buyHref: string;
}) {
  const product = products[0];
  const isOnSale = !!product.compare_at_cents && product.compare_at_cents > product.price_cents;
  return (
    <>
      <section className="hero">
        <div className="hero-glow glow-one" /><div className="hero-glow glow-two" />
        <div className="container hero-grid">
          <div className="hero-copy">
            <div className="eyebrow"><span /> COLEÇÃO OFICIAL 2026</div>
            <h1>Vista a energia.<br /><em>Carregue a identidade.</em></h1>
            <p>O uniforme que representa nossa comunidade dentro e fora da arena. Criado para quem vive o jogo intensamente.</p>
            <div className="hero-actions">
              <a className="primary-button" href={buyHref}>COMPRAR NA GRÁFICA <ArrowRight size={18} /></a>
              <div className="stock-proof"><span className="pulse" /> Edição limitada</div>
            </div>
            <div className="hero-proof">
              <div><ShieldCheck /><span><b>Compra protegida</b>Ambiente seguro</span></div>
              <div><Truck /><span><b>Envio nacional</b>Para todo o Brasil</span></div>
            </div>
          </div>
          <div className="hero-product">
            <div className="edition-stamp"><span>EDIÇÃO</span><b>01</b><small>/ 2026</small></div>
            <div className="orbit one" /><div className="orbit two" />
            <img src={product.front_image} alt={product.name} />
            <div className="floating-tag"><Zap size={17} fill="currentColor" /><span>TECIDO<br /><b>PERFORMANCE</b></span></div>
          </div>
        </div>
        <div className="ticker">
          <div>SGZ <span>✦</span> PERFORMANCE <span>✦</span> IDENTIDADE <span>✦</span> COMUNIDADE <span>✦</span> SGZ <span>✦</span> PERFORMANCE</div>
        </div>
      </section>

      <ChampionshipSpotlight />

      <TeamSection />

      <section className="featured container">
        <div className="section-heading">
          <div><span className="section-kicker">DROP 01</span><h2>O manto da <em>nova geração.</em></h2></div>
          <p>Design competitivo, conforto para o dia inteiro e cada detalhe pensado para representar.</p>
        </div>
        <div className="featured-grid">
          <button className="product-showcase" onClick={() => openProduct(product)}>
            <div className="limited-badge">LIMITED DROP</div>
            <img src={product.front_image} alt="Camisa SGZ frente" />
            <span className="view-product"><Eye size={17} /> VER DETALHES</span>
          </button>
          <div className="product-summary">
            <span className="label">UNIFORME OFICIAL</span>
            <h3>{product.name}</h3>
            <div className="rating">★★★★★ <span>Qualidade premium</span></div>
            <p>{product.description}</p>
            <ul className="feature-list">
              <li><Check /> Tecido dry-fit de alta respirabilidade</li>
              <li><Check /> Impressão sublimada de alta definição</li>
              <li><Check /> Modelagem unissex confortável</li>
              <li><Check /> Personalização com seu nickname</li>
            </ul>
            <div className="summary-price">
              <small>A partir de</small>
              {isOnSale && <del>{money(product.compare_at_cents!)}</del>}
              <b>{money(product.price_cents)}</b><span>ou em até 12x</span>
            </div>
            <a className="primary-button wide" href={buyHref}>COMPRAR NA GRÁFICA <ArrowRight /></a>
          </div>
        </div>
      </section>

      {products.length > 1 && (
        <section className="catalog container" id="catalogo">
          <div className="section-heading">
            <div><span className="section-kicker">CATÁLOGO SGZ</span><h2>Escolha o seu <em>manto.</em></h2></div>
            <p>Todos os produtos oficiais publicados pela equipe, prontos para personalizar e comprar.</p>
          </div>
          <div className="catalog-grid">
            {products.map((item) => (
              <article className="catalog-card" key={item.id}>
                <button className="catalog-image" onClick={() => openProduct(item)}>
                  <img src={item.front_image} alt={item.name} />
                  <span><Eye size={16} /> VER PRODUTO</span>
                </button>
                <div>
                  <small>{item.subtitle || "PRODUTO OFICIAL"}</small>
                  <h3>{item.name}</h3>
                  <p>{money(item.price_cents)}</p>
                  <button onClick={() => openProduct(item)}>ESCOLHER <ChevronRight /></button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="story" id="story">
        <div className="container story-grid">
          <div className="story-mark"><img src="/images/logo-sgz.png" alt="" /></div>
          <div>
            <span className="section-kicker">MAIS QUE UM TIME</span>
            <h2>Uma identidade que<br /><em>se move com você.</em></h2>
            <p>A SGZ nasceu da energia de quem compete, evolui e nunca joga sozinho. Nosso uniforme traduz essa atitude em cada linha.</p>
            <div className="story-numbers">
              <div><b>01</b><span>Design<br />exclusivo</span></div>
              <div><b>100%</b><span>Identidade<br />SGZ</span></div>
              <div><b>∞</b><span>Energia para<br />o próximo nível</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits container">
        <div><Truck /><b>ENVIO PARA TODO BRASIL</b><span>Acompanhe cada etapa do pedido</span></div>
        <div><ShieldCheck /><b>COMPRA SEGURA</b><span>Seus dados sempre protegidos</span></div>
        <div><CreditCard /><b>PAGAMENTO FLEXÍVEL</b><span>Pix e cartão após configuração</span></div>
        <div><Headphones /><b>SUPORTE PRÓXIMO</b><span>Atendimento direto da equipe</span></div>
      </section>
    </>
  );
}

function ChampionshipSpotlight() {
  return (
    <section className="championship" aria-labelledby="championship-title">
      <div className="championship-light championship-light-one" />
      <div className="championship-light championship-light-two" />
      <div className="container championship-grid">
        <a
          className="championship-poster"
          href={CHAMPIONSHIP_WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Falar no WhatsApp sobre a Copa Brasileira Wild Rift Season 2"
        >
          <span className="championship-live"><span /> INSCRIÇÕES ABERTAS</span>
          <img
            src="/images/campeonato-cbwr-season-2.png"
            alt="Cartaz da Copa Brasileira Wild Rift Season 2, com premiação total de mil reais"
          />
          <span className="championship-poster-cta"><MessageCircle /> CLIQUE E FALE COM A ORGANIZAÇÃO</span>
        </a>

        <div className="championship-copy">
          <div className="championship-kicker"><Trophy /> CAMPEONATO OFICIAL</div>
          <h2 id="championship-title">A arena está pronta.<br /><em>Sua equipe também?</em></h2>
          <p className="championship-lead">
            A <strong>Copa Brasileira Wild Rift — Season 2</strong> chegou. Monte sua line, entre na disputa
            e mostre sua força em busca do topo.
          </p>

          <div className="championship-prize">
            <span>PREMIAÇÃO TOTAL</span>
            <strong>R$ 1.000,00</strong>
            <small>1º lugar: R$ 700 • 2º lugar: R$ 300</small>
          </div>

          <div className="championship-details">
            <div><CalendarDays /><span><small>DATA DO EVENTO</small><b>15 DE SETEMBRO</b></span></div>
            <div><BadgeCheck /><span><small>INSCRIÇÃO</small><b>R$ 35,00</b></span></div>
          </div>

          <a className="championship-whatsapp" href={CHAMPIONSHIP_WHATSAPP_URL} target="_blank" rel="noreferrer">
            <MessageCircle />
            <span><small>INSCRIÇÕES E MAIS INFORMAÇÕES</small><b>(27) 99806-4179</b></span>
            <ArrowRight />
          </a>
          <p className="championship-hint">Clique na arte ou no botão para conversar diretamente pelo WhatsApp.</p>
        </div>
      </div>
    </section>
  );
}

const leaders = [
  { name: "Gomez", role: "Vice-líder", image: "/images/equipe/gomez-vice-lider.jpg" },
  { name: "SGZ Croc", role: "Fundador da SGZ", image: "/images/equipe/sgz-croc-fundador.jpg" },
  { name: "Cachinhos2", role: "Líder de recrutamento", image: "/images/equipe/cachinhos2-recrutamento.jpg" },
  { name: "Talita", role: "Líder de recrutamento", image: "/images/equipe/talita-recrutamento.jpg" },
  { name: "Felipe", role: "Moderação", image: "/images/equipe/felipe-moderacao.png" }
];

function TeamSection() {
  return (
    <section className="leadership" id="equipe">
      <div className="container">
        <div className="leadership-heading">
          <div><span className="section-kicker">ADMINISTRAÇÃO SGZ</span><h2>Quem move a <em>organização.</em></h2></div>
          <p>Visão, liderança e comunidade. Conheça quem constrói a SGZ dentro e fora da arena.</p>
        </div>
        <div className="leaders-grid">
          {leaders.map((leader, index) => (
            <article className="leader-card" key={leader.name}>
              <img src={leader.image} alt={`${leader.name}, ${leader.role}`} />
              <div className="leader-card-info">
                <span>0{index + 1}</span>
                <div><small>{leader.role}</small><b>{leader.name}</b></div>
              </div>
            </article>
          ))}
        </div>
        <div className="official-team">
          <div>
            <span className="section-kicker">SGZ ESPORTS</span>
            <h3>Uma equipe.<br /><em>Uma identidade.</em></h3>
            <p>Talento, estratégia e determinação reunidos para representar a organização no mais alto nível.</p>
            <span className="team-signature"><span /> JOGAMOS JUNTOS</span>
          </div>
          <div className="official-team-art">
            <img src="/images/equipe/equipe-oficial.jpg" alt="Apresentação da equipe oficial SGZ" />
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductGallery({ product }: { product: Product }) {
  const [image, setImage] = useState(product.front_image);
  const [zoom, setZoom] = useState(false);
  const [position, setPosition] = useState("50% 50%");
  const area = useRef<HTMLButtonElement>(null);
  const move = (event: React.MouseEvent) => {
    if (!area.current) return;
    const rect = area.current.getBoundingClientRect();
    setPosition(`${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`);
  };
  return (
    <div className="gallery">
      <div className="thumbnails">
        {[product.front_image, product.back_image].map((src, index) => (
          <button className={image === src ? "active" : ""} onClick={() => setImage(src)} key={src}>
            <img src={src} alt={index ? "Costas da camisa" : "Frente da camisa"} />
            <span>{index ? "COSTAS" : "FRENTE"}</span>
          </button>
        ))}
      </div>
      <button className="main-image" ref={area} onMouseMove={move} onClick={() => setZoom(true)}>
        <div className="zoom-hint"><Expand size={17} /> CLIQUE PARA AMPLIAR</div>
        <img src={image} alt={product.name} />
      </button>
      {zoom && (
        <div className="zoom-modal" onClick={() => setZoom(false)}>
          <button aria-label="Fechar"><X /></button>
          <div className="zoomed" style={{ backgroundImage: `url(${image})`, backgroundPosition: position }} />
          <span>Mova o cursor sobre a imagem para explorar os detalhes</span>
        </div>
      )}
    </div>
  );
}

function ProductPage({ product, buyHref }: { product: Product; buyHref: string }) {
  const isOnSale = !!product.compare_at_cents && product.compare_at_cents > product.price_cents;
  return (
    <section className="product-page container">
      <div className="breadcrumbs">INÍCIO <ChevronRight size={14} /> UNIFORME OFICIAL</div>
      <div className="product-grid">
        <ProductGallery product={product} />
        <div className="product-buybox">
          <span className="label">DROP 01 — EDIÇÃO LIMITADA</span>
          <h1>{product.name}</h1>
          <p className="product-subtitle">{product.subtitle}</p>
          <div className="rating">★★★★★ <span>Produto oficial SGZ</span></div>
          <div className="price-block">
            <div className="price-stack">
              {isOnSale && <del>{money(product.compare_at_cents!)}</del>}
              <b>{money(product.price_cents)}</b>
            </div>
            <span>ou 12x de {money(Math.ceil(product.price_cents / 12))}</span>
          </div>
          <div className="divider" />
          <p className="external-purchase-note">Tamanho, nickname, frete e pagamento são escolhidos diretamente na gráfica.</p>
          <a className="primary-button wide" href={buyHref}>
            <ShoppingBag /> COMPRAR NA GRÁFICA <ArrowRight />
          </a>
          <div className="buy-benefits">
            <span><ShieldCheck /> Compra protegida</span><span><PackageCheck /> Produto oficial</span><span><Truck /> Envio nacional</span>
          </div>
          <details open><summary>DETALHES DO PRODUTO <ChevronDown /></summary><p>{product.description}</p></details>
          <details><summary>CUIDADOS E COMPOSIÇÃO <ChevronDown /></summary><p>Lave do avesso, em ciclo delicado e com cores similares. Não passe sobre a estampa.</p></details>
        </div>
      </div>
    </section>
  );
}

function CartPage({ cart, subtotal, navigate, setCart }: {
  cart: CartItem[]; subtotal: number; navigate: (page: Page) => void;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
}) {
  const change = (index: number, amount: number) =>
    setCart((items) => items.map((item, i) => i === index ? { ...item, quantity: Math.max(1, Math.min(5, item.quantity + amount)) } : item));
  if (!cart.length) return (
    <section className="empty-state container"><ShoppingBag /><h1>Sua sacola está vazia</h1><p>O manto SGZ está esperando por você.</p>
      <button className="primary-button" onClick={() => navigate("product")}>CONHECER O UNIFORME</button>
    </section>
  );
  return (
    <section className="cart-page container">
      <div className="page-title"><span className="section-kicker">SEU PEDIDO</span><h1>Sacola de compras</h1></div>
      <div className="cart-layout">
        <div className="cart-items">
          {cart.map((item, index) => (
            <article className="cart-item" key={`${item.size}-${item.nickname}`}>
              <img src={item.image} alt={item.name} />
              <div className="cart-details"><small>UNIFORME OFICIAL</small><h2>{item.name}</h2>
                <p>Tamanho: <b>{item.size}</b>{item.nickname && <> · Nickname: <b>{item.nickname}</b></>}</p>
                <div className="quantity compact">
                  <button onClick={() => change(index, -1)}><Minus /></button><span>{item.quantity}</span><button onClick={() => change(index, 1)}><Plus /></button>
                </div>
              </div>
              <div className="cart-price"><b>{money(item.unitPrice * item.quantity)}</b>
                <button onClick={() => setCart((items) => items.filter((_, i) => i !== index))}>REMOVER</button>
              </div>
            </article>
          ))}
          <button className="text-link" onClick={() => navigate("product")}><ArrowLeft /> CONTINUAR COMPRANDO</button>
        </div>
        <aside className="order-summary">
          <h2>RESUMO DO PEDIDO</h2>
          <div><span>Subtotal</span><b>{money(subtotal)}</b></div>
          <div><span>Frete</span><span>Calculado no checkout</span></div>
          <hr /><div className="summary-total"><span>Total</span><b>{money(subtotal)}</b></div>
          <button className="primary-button wide" onClick={() => navigate("checkout")}>IR PARA O CHECKOUT <ArrowRight /></button>
          <p><LockKeyhole /> Compra segura e dados protegidos</p>
        </aside>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required = true, placeholder, className = "" }: {
  label: string; name: string; value: string; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string; required?: boolean; placeholder?: string; className?: string;
}) {
  return <label className={`field ${className}`}><span>{label}{required && " *"}</span><input name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} /></label>;
}

function AuthPage({ onAuthenticated }: { onAuthenticated: (user: User) => void }) {
  const registering = location.pathname.startsWith("/cadastro");
  const [mode, setMode] = useState<"login" | "register">(registering ? "register" : "login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", cpf: "", phone: "", birth_date: "", postal_code: "",
    street: "", number: "", complement: "", neighborhood: "", city: "", state: ""
  });
  const change = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target; let value = event.target.value;
    if (name === "cpf") value = maskCpf(value);
    if (name === "phone") value = maskPhone(value);
    if (name === "postal_code") value = maskCep(value);
    if (name === "state") value = value.toUpperCase().slice(0, 2);
    setForm((current) => ({ ...current, [name]: value }));
  };
  const findCep = async () => {
    if (onlyDigits(form.postal_code).length !== 8) return;
    try {
      const data = await api<{ street: string; neighborhood: string; city: string; state: string }>(`/cep/${onlyDigits(form.postal_code)}`);
      setForm((current) => ({ ...current, ...data }));
    } catch { /* The address remains manually editable. */ }
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const result = await api<{ user: User }>(`/auth/${mode}`, { method: "POST", body: JSON.stringify(body) });
      onAuthenticated(result.user);
    } catch (caught) { setError((caught as Error).message); } finally { setBusy(false); }
  };
  return (
    <section className="auth-page">
      <div className="auth-visual"><img src="/images/camisa-costas.png" alt="Camisa SGZ" /><div><span>MEMBRO DA COMUNIDADE</span><h2>Seu uniforme.<br />Sua identidade.</h2></div></div>
      <div className="auth-panel">
        <div className="auth-card">
          <span className="section-kicker">{mode === "login" ? "BEM-VINDO DE VOLTA" : "FAÇA PARTE"}</span>
          <h1>{mode === "login" ? "Acesse sua conta" : "Crie sua conta SGZ"}</h1>
          <p>{mode === "login" ? "Entre para acompanhar pedidos e finalizar sua compra." : "O cadastro completo é obrigatório para emissão e envio pelos Correios."}</p>
          <div className="auth-tabs"><button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>ENTRAR</button><button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>CRIAR CONTA</button></div>
          <form onSubmit={submit}>
            {mode === "register" && <Field label="Nome completo" name="full_name" value={form.full_name} onChange={change} />}
            <Field label="E-mail" name="email" type="email" value={form.email} onChange={change} />
            <label className="field"><span>Senha *</span><div className="password-input"><input name="password" type={showPassword ? "text" : "password"} minLength={8} value={form.password} onChange={change} required /><button type="button" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</button></div></label>
            {mode === "register" && (
              <>
                <div className="form-grid two"><Field label="CPF" name="cpf" value={form.cpf} onChange={change} /><Field label="Celular" name="phone" value={form.phone} onChange={change} /></div>
                <Field label="Data de nascimento" name="birth_date" type="date" value={form.birth_date} onChange={change} required={false} />
                <div className="form-section-title"><Truck /> ENDEREÇO DE ENTREGA</div>
                <Field label="CEP" name="postal_code" value={form.postal_code} onChange={change} />
                <button type="button" className="cep-link" onClick={findCep}>PREENCHER ENDEREÇO PELO CEP</button>
                <div className="form-grid street"><Field label="Rua / Avenida" name="street" value={form.street} onChange={change} /><Field label="Número" name="number" value={form.number} onChange={change} /></div>
                <div className="form-grid two"><Field label="Complemento" name="complement" value={form.complement} onChange={change} required={false} /><Field label="Bairro" name="neighborhood" value={form.neighborhood} onChange={change} /></div>
                <div className="form-grid city"><Field label="Cidade" name="city" value={form.city} onChange={change} /><Field label="UF" name="state" value={form.state} onChange={change} /></div>
                <p className="privacy-note"><ShieldCheck /> Seu CPF é armazenado de forma criptografada e usado apenas no processamento do pedido.</p>
              </>
            )}
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button wide" disabled={busy}>{busy ? "PROCESSANDO..." : mode === "login" ? "ENTRAR NA CONTA" : "CRIAR CONTA E CONTINUAR"}</button>
          </form>
        </div>
      </div>
    </section>
  );
}

function AuthGate({ navigate, admin = false }: { navigate: (page: Page) => void; admin?: boolean }) {
  return <section className="empty-state container"><LockKeyhole /><h1>{admin ? "Área restrita" : "Entre para continuar"}</h1><p>{admin ? "Este painel é exclusivo para administradores." : "Seu cadastro completo é necessário antes da compra."}</p><button className="primary-button" onClick={() => navigate("auth")}>ENTRAR OU CRIAR CONTA</button></section>;
}

function CheckoutPage({ cart, subtotal, user, settings, navigate, onComplete }: {
  cart: CartItem[]; subtotal: number; user: User | null; settings: Record<string, string>;
  navigate: (page: Page) => void; onComplete: () => void;
}) {
  const [payment, setPayment] = useState<"pix" | "card">("pix");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<{ order_number: string; pix_key?: string; card_url?: string; total_cents: number } | null>(null);
  const shipping = Number(settings.shipping_flat_cents || 0);
  if (!user) return <AuthGate navigate={navigate} />;
  if (!cart.length && !order) return <AuthGate navigate={navigate} />;
  const finish = async () => {
    setBusy(true); setError("");
    try {
      const result = await api<{ order: { order_number: string; pix_key?: string; card_url?: string; total_cents: number } }>("/orders", {
        method: "POST", body: JSON.stringify({ items: cart, payment_method: payment })
      });
      setOrder(result.order); onComplete();
    } catch (caught) { setError((caught as Error).message); } finally { setBusy(false); }
  };
  if (order) return (
    <section className="success-page container">
      <div className="success-icon"><Check /></div><span className="section-kicker">PEDIDO RECEBIDO</span>
      <h1>Seu pedido <em>entrou em jogo.</em></h1><p>Número <b>#{order.order_number}</b>. Acompanhe o andamento na sua conta.</p>
      <div className="payment-instructions">
        <Banknote /><div><b>{payment === "pix" ? "PAGAMENTO VIA PIX" : "PAGAMENTO VIA CARTÃO"}</b>
          {payment === "pix" ? (
            order.pix_key ? <><p>Envie {money(order.total_cents)} para a chave abaixo:</p><code>{order.pix_key}</code></> :
              <p>A equipe SGZ enviará as instruções de Pix. O pedido permanece aguardando pagamento.</p>
          ) : order.card_url ? <a href={order.card_url} target="_blank" rel="noreferrer">ABRIR PAGAMENTO SEGURO</a> :
            <p>A equipe SGZ enviará o link seguro de pagamento. Nenhum dado de cartão é armazenado neste site.</p>}
        </div>
      </div>
      <button className="primary-button" onClick={() => navigate("account")}>ACOMPANHAR MEU PEDIDO</button>
    </section>
  );
  return (
    <section className="checkout-page container">
      <div className="page-title"><span className="section-kicker">FINALIZAÇÃO SEGURA</span><h1>Checkout</h1></div>
      <div className="checkout-layout">
        <div>
          <div className="checkout-card"><div className="checkout-heading"><span>1</span><div><b>DADOS DO CLIENTE</b><small>Cadastro confirmado</small></div><BadgeCheck /></div>
            <p><b>{user.full_name}</b><br />{user.email} · {user.phone}<br />CPF {user.cpf_masked}</p>
          </div>
          <div className="checkout-card"><div className="checkout-heading"><span>2</span><div><b>ENDEREÇO DE ENTREGA</b><small>Envio pelos Correios</small></div><Truck /></div>
            <p><b>{user.street}, {user.number}</b>{user.complement ? ` — ${user.complement}` : ""}<br />{user.neighborhood}, {user.city} — {user.state}<br />CEP {maskCep(user.postal_code)}</p>
          </div>
          <div className="checkout-card"><div className="checkout-heading"><span>3</span><div><b>FORMA DE PAGAMENTO</b><small>Escolha como pagar</small></div><CreditCard /></div>
            <div className="payment-options">
              <button className={payment === "pix" ? "active" : ""} onClick={() => setPayment("pix")}><Banknote /><span><b>PIX</b><small>Confirmação pela equipe</small></span><i>{payment === "pix" && <Check />}</i></button>
              <button className={payment === "card" ? "active" : ""} onClick={() => setPayment("card")}><CreditCard /><span><b>CARTÃO</b><small>Link seguro de pagamento</small></span><i>{payment === "card" && <Check />}</i></button>
            </div>
            <p className="payment-safety"><LockKeyhole /> O site nunca armazena os dados do seu cartão.</p>
          </div>
        </div>
        <aside className="order-summary checkout-summary">
          <h2>SEU PEDIDO</h2>
          {cart.map((item) => <div className="checkout-item" key={`${item.size}-${item.nickname}`}><img src={item.image} alt="" /><span><b>{item.name}</b><small>{item.quantity}× · Tam. {item.size}{item.nickname ? ` · ${item.nickname}` : ""}</small></span><strong>{money(item.unitPrice * item.quantity)}</strong></div>)}
          <hr /><div><span>Subtotal</span><b>{money(subtotal)}</b></div><div><span>Frete</span><b>{shipping ? money(shipping) : "Grátis"}</b></div>
          <hr /><div className="summary-total"><span>Total</span><b>{money(subtotal + shipping)}</b></div>
          {error && <div className="form-error">{error}</div>}
          <button className="primary-button wide" disabled={busy} onClick={finish}>{busy ? "CRIANDO PEDIDO..." : "CONFIRMAR PEDIDO"} <ArrowRight /></button>
          <p><ShieldCheck /> Ao confirmar, você aceita os termos da loja.</p>
        </aside>
      </div>
    </section>
  );
}

function AccountPage({ user, setUser, navigate }: { user: User; setUser: (user: User | null) => void; navigate: (page: Page) => void }) {
  const [orders, setOrders] = useState<Order[]>([]);
  useEffect(() => { api<{ orders: Order[] }>("/orders/my").then((data) => setOrders(data.orders)); }, []);
  const logout = async () => { await api("/auth/logout", { method: "POST" }); setUser(null); navigate("home"); };
  return (
    <section className="account-page container">
      <aside className="account-sidebar"><div className="account-avatar">{user.full_name[0]}</div><h2>{user.full_name}</h2><p>{user.email}</p>
        <nav><button className="active"><PackageCheck /> Meus pedidos</button>{user.role === "admin" && <button onClick={() => navigate("admin")}><LayoutDashboard /> Administração</button>}<button onClick={logout}><LogOut /> Sair</button></nav>
      </aside>
      <div className="account-content"><span className="section-kicker">MINHA CONTA</span><h1>Meus pedidos</h1>
        {!orders.length ? <div className="no-orders"><Box /><b>Nenhum pedido por aqui ainda</b><button onClick={() => navigate("product")}>CONHECER O UNIFORME</button></div> :
          <div className="orders-list">{orders.map((order) => {
            const items = JSON.parse(order.items_json) as CartItem[];
            return <article className="order-card" key={order.id}><header><span><small>PEDIDO</small><b>#{order.order_number}</b></span><span><small>DATA</small><b>{new Date(`${order.created_at}Z`).toLocaleDateString("pt-BR")}</b></span><span><small>TOTAL</small><b>{money(order.total_cents)}</b></span><i className={`status ${order.status}`}>{statusLabel[order.status]}</i></header>
              <div><img src={items[0]?.image} alt="" /><span><b>{items[0]?.name}</b><small>{items.map((item) => `${item.quantity}× ${item.size}${item.nickname ? ` / ${item.nickname}` : ""}`).join(", ")}</small>{order.tracking_code && <small>Rastreio: <b>{order.tracking_code}</b></small>}</span><span className="payment-chip">{statusLabel[order.payment_status] || order.payment_status}</span></div>
            </article>;
          })}</div>}
      </div>
    </section>
  );
}

const billingStatusLabel: Record<string, string> = {
  creating: "Criando",
  pending: "Aguardando pagamento",
  confirmed: "Confirmado",
  paid: "Pago",
  overdue: "Vencido",
  canceled: "Cancelado",
  refunded: "Estornado"
};

function billingDate(value: string) {
  if (!value) return "—";
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return Number.isFinite(parsed.getTime())
    ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(parsed)
    : value;
}

function BillingPixPanel() {
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState({
    contactName: "", cpfCnpj: "", email: "", phone: "", billingDay: "10"
  });

  const applyBilling = (next: BillingData) => {
    setData(next);
    setProfile((current) => ({
      contactName: next.client.contactName || current.contactName,
      cpfCnpj: "",
      email: next.client.email || current.email,
      phone: next.client.phone || current.phone,
      billingDay: next.client.billingDay ? String(next.client.billingDay) : current.billingDay
    }));
  };

  const requestBilling = async (body?: unknown) => {
    const result = await api<{ ok: boolean; data?: BillingData; error?: string }>("/admin/billing", {
      method: body ? "POST" : "GET",
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!result.ok || !result.data) {
      throw new Error(result.error || "Não foi possível carregar a mensalidade.");
    }
    applyBilling(result.data);
    return result.data;
  };

  const refreshBilling = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      await requestBilling({ action: "refresh" });
      if (!quiet) setFeedback({ kind: "success", text: "Status atualizado com o Chama Pedido." });
    } catch (error) {
      setFeedback({ kind: "error", text: error instanceof Error ? error.message : "Não foi possível atualizar." });
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    api<{ ok: boolean; data?: BillingData; error?: string }>("/admin/billing")
      .then((result) => {
        if (!active) return;
        if (!result.ok || !result.data) {
          throw new Error(result.error || "Não foi possível carregar a mensalidade.");
        }
        applyBilling(result.data);
      })
      .catch((error: unknown) => {
        if (active) {
          setFeedback({
            kind: "error",
            text: error instanceof Error ? error.message : "Não foi possível carregar."
          });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (
      !data?.currentCharge ||
      !["creating", "pending", "confirmed", "overdue"].includes(data.currentCharge.status)
    ) return;
    const timer = window.setInterval(() => void refreshBilling(true), 15_000);
    return () => window.clearInterval(timer);
  }, [data?.currentCharge?.id, data?.currentCharge?.status]);

  const saveProfile = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await requestBilling({
        action: "save_profile",
        profile: { ...profile, billingDay: Number(profile.billingDay) }
      });
      setFeedback({ kind: "success", text: "Dados de cobrança salvos com segurança." });
    } catch (error) {
      setFeedback({ kind: "error", text: error instanceof Error ? error.message : "Não foi possível salvar." });
    } finally {
      setSaving(false);
    }
  };

  const activateBilling = async () => {
    setActivating(true);
    setFeedback(null);
    try {
      await requestBilling({ action: "create_pix_subscription" });
      setFeedback({
        kind: "success",
        text: "Mensalidades ativadas. O primeiro Pix já está disponível."
      });
    } catch (error) {
      setFeedback({ kind: "error", text: error instanceof Error ? error.message : "Não foi possível gerar o Pix." });
    } finally {
      setActivating(false);
    }
  };

  const copyPix = async () => {
    if (!data?.currentCharge?.payload) return;
    await navigator.clipboard.writeText(data.currentCharge.payload);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  if (loading && !data) {
    return (
      <div className="billing-loading">
        <span />
        <b>Conectando ao Chama Pedido</b>
        <small>Consultando sua mensalidade com segurança.</small>
      </div>
    );
  }

  const charge = data?.currentCharge || null;
  const qrSource = charge?.encodedImage
    ? charge.encodedImage.startsWith("data:image")
      ? charge.encodedImage
      : `data:image/png;base64,${charge.encodedImage}`
    : "";
  const profileReady = Boolean(
    data?.client.hasDocument &&
    data.client.contactName &&
    data.client.phone &&
    data.client.billingDay
  );
  const chargeOpen = Boolean(
    charge && ["creating", "pending", "confirmed", "overdue"].includes(charge.status)
  );

  return (
    <div className="billing-pix">
      <section className="billing-pix-hero">
        <div>
          <span className="section-kicker">MENSALIDADE DO SISTEMA</span>
          <div className="billing-price">
            <strong>{money(data?.monthlyAmountCents || 5000)}</strong>
            <small>por mês</small>
          </div>
          <p>O Asaas gera um Pix dinâmico em cada vencimento. Depois do pagamento, a baixa aparece automaticamente no Chama Pedido.</p>
        </div>
        <div className={`billing-headline ${charge?.status === "overdue" ? "danger" : chargeOpen ? "warning" : data?.subscription.active ? "success" : ""}`}>
          <QrCode />
          <span>
            <b>{chargeOpen ? charge?.status === "overdue" ? "Mensalidade vencida" : "Pix disponível" : data?.subscription.active ? "Cobranças mensais ativas" : "Aguardando configuração"}</b>
            <small>{chargeOpen ? `Vencimento em ${billingDate(charge?.dueDate || "")}.` : data?.subscription.active ? `Novo Pix todo mês, no dia ${data.subscription.billingDay}.` : "Complete os dados do responsável abaixo."}</small>
          </span>
        </div>
      </section>

      {feedback && (
        <div className={`billing-feedback ${feedback.kind}`}>
          {feedback.kind === "success" ? <Check /> : <X />}
          {feedback.text}
        </div>
      )}

      <div className="billing-columns">
        <section className="billing-card">
          <header>
            <div><span className="section-kicker">DADOS DO PAGADOR</span><h2>Responsável pela SGZ</h2></div>
            {data?.client.hasDocument && <i>CADASTRO PRONTO</i>}
          </header>
          <div className="billing-form">
            <label className="wide"><span>Nome do responsável</span><input value={profile.contactName} onChange={(event) => setProfile({ ...profile, contactName: event.target.value })} placeholder="Nome completo" /></label>
            <label><span>CPF ou CNPJ</span><input value={profile.cpfCnpj} onChange={(event) => setProfile({ ...profile, cpfCnpj: event.target.value })} inputMode="numeric" placeholder={data?.client.documentMasked || "Somente números"} />{data?.client.documentMasked && <small>Salvo: {data.client.documentMasked}. Deixe vazio para manter.</small>}</label>
            <label><span>Dia de vencimento</span><select value={profile.billingDay} onChange={(event) => setProfile({ ...profile, billingDay: event.target.value })}>{Array.from({ length: 28 }, (_, index) => index + 1).map((day) => <option key={day} value={day}>Todo dia {day}</option>)}</select></label>
            <label><span>WhatsApp</span><input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} inputMode="tel" placeholder="(00) 00000-0000" /></label>
            <label><span>E-mail</span><input value={profile.email} onChange={(event) => setProfile({ ...profile, email: event.target.value })} type="email" placeholder="financeiro@sgz.com.br" /></label>
          </div>
          <button className="billing-secondary" disabled={saving} onClick={() => void saveProfile()}>{saving ? "SALVANDO..." : "SALVAR DADOS DE COBRANÇA"}</button>
        </section>

        <section className="billing-card">
          <header><div><span className="section-kicker">PAGAMENTO DA MENSALIDADE</span><h2>Pix com vencimento</h2></div></header>
          {chargeOpen && charge ? (
            <div className="billing-charge">
              <div className="billing-qr">{qrSource ? <img src={qrSource} alt="QR Code Pix da mensalidade SGZ" /> : <span>PIX<br />COPIA E COLA</span>}</div>
              <div>
                <i className={charge.status === "overdue" ? "danger" : "warning"}>{billingStatusLabel[charge.status] || charge.status}</i>
                <strong>{money(charge.amountCents)}</strong>
                <p>Vencimento em {billingDate(charge.dueDate)}. Leia o QR Code ou copie o código Pix.</p>
                <div className="billing-actions">
                  {charge.payload && <button className="billing-primary" onClick={() => void copyPix()}><Copy />{copied ? "PIX COPIADO" : "COPIAR PIX"}</button>}
                  {charge.invoiceUrl && <a href={charge.invoiceUrl} target="_blank" rel="noreferrer">ABRIR COBRANÇA</a>}
                </div>
              </div>
            </div>
          ) : data?.subscription.active ? (
            <div className="billing-programmed"><Check /><b>Mensalidades programadas</b><p>O Asaas gera um novo Pix todo mês no dia {data.subscription.billingDay}. Nenhum valor é retirado automaticamente da conta.</p></div>
          ) : (
            <ol className="billing-steps">
              <li><b>1</b><span>Salve os dados do responsável e escolha o vencimento.</span></li>
              <li><b>2</b><span>O Asaas gera um QR Code diferente para cada mensalidade.</span></li>
              <li><b>3</b><span>Depois do pagamento, o Chama Pedido confirma a baixa.</span></li>
            </ol>
          )}
          {!data?.subscription.active && <button className="billing-primary wide" disabled={!data?.providerConfigured || !profileReady || activating} onClick={() => void activateBilling()}><QrCode />{activating ? "GERANDO PRIMEIRA MENSALIDADE..." : "ATIVAR MENSALIDADES POR PIX"}</button>}
          {!profileReady && !data?.subscription.active && <small className="billing-hint">Salve os dados do pagador para liberar a primeira cobrança.</small>}
        </section>
      </div>

      <section className="billing-card billing-history">
        <header>
          <div><span className="section-kicker">BOLETO E PIX</span><h2>Histórico de mensalidades</h2><p>O status muda automaticamente quando o Asaas confirma o pagamento.</p></div>
          <button className="billing-secondary compact" disabled={loading} onClick={() => void refreshBilling()}><RefreshCw />{loading ? "ATUALIZANDO" : "ATUALIZAR STATUS"}</button>
        </header>
        {data?.invoices.length ? (
          <div className="billing-invoices">{data.invoices.map((invoice) => (
            <article key={invoice.id}>
              <span><b>Mensalidade · {billingDate(invoice.dueDate)}</b><small>PIX DINÂMICO</small></span>
              <strong>{money(invoice.amountCents)}</strong>
              <i className={invoice.status === "paid" ? "success" : invoice.status === "overdue" ? "danger" : "warning"}>{billingStatusLabel[invoice.status] || invoice.status}</i>
              {invoice.invoiceUrl && invoice.status !== "paid" && <a href={invoice.invoiceUrl} target="_blank" rel="noreferrer">ABRIR</a>}
            </article>
          ))}</div>
        ) : (
          <div className="billing-empty"><QrCode /><b>Nenhuma mensalidade registrada ainda</b><small>O primeiro Pix aparecerá aqui depois da ativação.</small></div>
        )}
      </section>
    </div>
  );
}

function AdminPage({ store, refreshStore }: { store: Storefront; refreshStore: () => Promise<void> }) {
  const [tab, setTab] = useState<"overview" | "orders" | "product" | "settings" | "billing">(
    () => new URLSearchParams(location.search).get("tab") === "billing" ? "billing" : "overview"
  );
  const [data, setData] = useState<{ metrics: Record<string, number>; orders: Order[] } | null>(null);
  const [products, setProducts] = useState<Product[]>(store.products || [store.product]);
  const [message, setMessage] = useState("");
  const [product, setProduct] = useState<ProductDraft>(() => productDraft(store.product));
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    pix_key: store.settings.pix_key || "", pix_holder: store.settings.pix_holder || "",
    card_payment_url: store.settings.card_payment_url || "", whatsapp: store.settings.whatsapp || "",
    instagram: store.settings.instagram || "", support_email: store.settings.support_email || "",
    announcement: store.settings.announcement || "", shipping_flat: (Number(store.settings.shipping_flat_cents || 0) / 100).toFixed(2).replace(".", ",")
  });
  const load = () => api<{ metrics: Record<string, number>; orders: Order[] }>("/admin/dashboard").then(setData);
  const loadProducts = async (selectId?: string) => {
    const result = await api<{ products: Product[] }>("/admin/products");
    setProducts(result.products);
    const selected = result.products.find((item) => item.id === selectId);
    if (selected) setProduct(productDraft(selected));
    return result.products;
  };
  useEffect(() => { load(); loadProducts(product.id || undefined); }, []);
  const chooseProduct = (selected?: Product) => {
    setProduct(productDraft(selected));
    setFrontFile(null); setBackFile(null); setFrontPreview(""); setBackPreview(""); setMessage("");
  };
  const chooseImage = (side: "front" | "back", file?: File) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (side === "front") { setFrontFile(file); setFrontPreview(preview); }
    else { setBackFile(file); setBackPreview(preview); }
  };
  const saveProduct = async () => {
    if (!product.id && (!frontFile || !backFile)) {
      setMessage("Adicione as fotos da frente e das costas do novo produto.");
      return;
    }
    setSaving(true); setMessage("Salvando produto e preparando as imagens...");
    try {
      let productId = product.id;
      if (!productId) {
        const created = await api<{ id: string }>("/admin/products", {
          method: "POST",
          body: JSON.stringify({ ...product, active: false })
        });
        productId = created.id;
      }
      if (frontFile) {
        const image = await optimizeProductImage(frontFile);
        await apiUpload(`/admin/products/${productId}/images/front`, image);
      }
      if (backFile) {
        const image = await optimizeProductImage(backFile);
        await apiUpload(`/admin/products/${productId}/images/back`, image);
      }
      await api(`/admin/products/${productId}`, { method: "PUT", body: JSON.stringify(product) });
      await Promise.all([refreshStore(), loadProducts(productId)]);
      setFrontFile(null); setBackFile(null); setFrontPreview(""); setBackPreview("");
      setMessage(product.id ? "Produto e estoque atualizados." : "Novo produto adicionado à loja.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o produto.");
    } finally {
      setSaving(false);
    }
  };
  const saveSettings = async () => {
    await api("/admin/settings", { method: "PUT", body: JSON.stringify(settings) });
    await refreshStore(); setMessage("Configurações salvas.");
  };
  const updateOrder = async (id: string, status: string, payment_status: string) => {
    await api(`/admin/orders/${id}`, { method: "PUT", body: JSON.stringify({ status, payment_status }) }); await load();
  };
  if (!data) return <Loading />;
  const tabTitle = tab === "overview"
    ? "Visão geral"
    : tab === "orders"
      ? "Pedidos"
      : tab === "product"
        ? "Catálogo de produtos"
        : tab === "settings"
          ? "Configurações"
          : "Boleto / Pix";
  return (
    <section className="admin-shell">
      <aside className="admin-nav"><div className="admin-brand"><img src="/images/logo-sgz.png" alt="" /><span><b>SGZ</b> ADMIN</span></div>
        <nav>
          <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}><LayoutDashboard /> Visão geral</button>
          <button className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")}><ShoppingCart /> Pedidos</button>
          <button className={tab === "product" ? "active" : ""} onClick={() => setTab("product")}><PackageCheck /> Produtos</button>
          <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")}><Pencil /> Configurações</button>
          <button className={tab === "billing" ? "active" : ""} onClick={() => setTab("billing")}><QrCode /> Boleto / Pix</button>
        </nav>
      </aside>
      <div className="admin-main">
        <header><div><span className="section-kicker">PAINEL DE CONTROLE</span><h1>{tabTitle}</h1></div><span className="online-dot">LOJA ONLINE</span></header>
        {message && <div className="admin-message"><Check /> {message}</div>}
        {tab === "overview" && <><div className="metrics">
          <div><ShoppingCart /><span><small>PEDIDOS TOTAIS</small><b>{data.metrics.orders || 0}</b></span></div>
          <div><Banknote /><span><small>RECEITA PAGA</small><b>{money(data.metrics.revenue || 0)}</b></span></div>
          <div><PackageCheck /><span><small>AGUARDANDO</small><b>{data.metrics.pending || 0}</b></span></div>
          <div><UserRound /><span><small>CLIENTES</small><b>{data.metrics.customers || 0}</b></span></div>
        </div><RecentOrders orders={data.orders.slice(0, 6)} update={updateOrder} /></>}
        {tab === "orders" && <RecentOrders orders={data.orders} update={updateOrder} />}
        {tab === "product" && <>
          <div className="admin-products-toolbar">
            <div><b>{products.length} {products.length === 1 ? "produto cadastrado" : "produtos cadastrados"}</b><span>Gerencie fotos, valores, estoque e publicação.</span></div>
            <button className="primary-button" onClick={() => chooseProduct()}><Plus /> NOVO PRODUTO</button>
          </div>
          <div className="admin-catalog-layout">
            <aside className="admin-product-list">
              {products.map((item) => (
                <button className={product.id === item.id ? "active" : ""} key={item.id} onClick={() => chooseProduct(item)}>
                  <img src={item.front_image} alt="" />
                  <span><b>{item.name}</b><small>{money(item.price_cents)} · {item.active ? "Publicado" : "Oculto"}</small></span>
                  <ChevronRight />
                </button>
              ))}
            </aside>
            <div className="admin-product-editor">
              <div className="editor-heading">
                <div><span className="section-kicker">{product.id ? "EDITANDO PRODUTO" : "NOVO PRODUTO"}</span><h2>{product.id ? product.name : "Adicionar à loja"}</h2></div>
                <span className={`publish-status ${product.active ? "active" : ""}`}>{product.active ? "PUBLICADO" : "RASCUNHO"}</span>
              </div>
              <div className="admin-form">
                <div className="admin-images-column">
                  <div className="admin-product-preview"><img src={frontPreview || product.front_image} alt="Prévia da frente" /><span>FRENTE</span></div>
                  <div className="image-upload-grid">
                    <label className="image-upload"><Plus /><span><b>Foto da frente</b><small>PNG, JPG ou WebP</small></span><input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => chooseImage("front", e.target.files?.[0])} /></label>
                    <label className="image-upload"><Plus /><span><b>Foto das costas</b><small>PNG, JPG ou WebP</small></span><input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => chooseImage("back", e.target.files?.[0])} /></label>
                  </div>
                  <div className="back-preview"><img src={backPreview || product.back_image} alt="Prévia das costas" /><span>COSTAS</span></div>
                </div>
                <div className="admin-fields">
                  <Field label="Nome do produto" name="name" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} placeholder="Ex.: Camisa SGZ Away" />
                  <Field label="Subtítulo" name="subtitle" value={product.subtitle} onChange={(e) => setProduct({ ...product, subtitle: e.target.value })} placeholder="Ex.: Edição Competitiva 2026" />
                  <label className="field"><span>Descrição</span><textarea value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} placeholder="Descreva tecido, acabamento e diferenciais." /></label>
                  <div className="form-grid two"><Field label="Preço (R$)" name="price" value={product.price} onChange={(e) => setProduct({ ...product, price: e.target.value })} placeholder="115,00" /><Field label="Adicional nickname (R$)" name="personalization_price" value={product.personalization_price} onChange={(e) => setProduct({ ...product, personalization_price: e.target.value })} /></div>
                  <div className="inventory-heading"><b>ESTOQUE POR TAMANHO</b><span>{Object.values(product.inventory).reduce((sum, stock) => sum + Number(stock || 0), 0)} unidades no total</span></div>
                  <div className="inventory-grid">{Object.entries(product.inventory).map(([size, stock]) => <Field key={size} label={`Estoque ${size}`} name={size} type="number" value={String(stock)} onChange={(e) => setProduct({ ...product, inventory: { ...product.inventory, [size]: Number(e.target.value) } })} />)}</div>
                  <label className="switch-line"><input type="checkbox" checked={product.personalization_enabled} onChange={(e) => setProduct({ ...product, personalization_enabled: e.target.checked })} /><span /> Permitir personalização com nickname</label>
                  <label className="switch-line"><input type="checkbox" checked={product.active} onChange={(e) => setProduct({ ...product, active: e.target.checked })} /><span /> Produto visível e disponível para compra</label>
                  <button className="primary-button wide" disabled={saving} onClick={saveProduct}>{saving ? "SALVANDO..." : product.id ? "SALVAR ALTERAÇÕES" : "ADICIONAR PRODUTO"}</button>
                </div>
              </div>
            </div>
          </div>
        </>}
        {tab === "settings" && <div className="admin-fields settings-form">
          <div className="settings-section"><h2>PAGAMENTOS</h2><Field label="Chave Pix" name="pix_key" value={settings.pix_key} onChange={(e) => setSettings({ ...settings, pix_key: e.target.value })} required={false} /><Field label="Nome do recebedor" name="pix_holder" value={settings.pix_holder} onChange={(e) => setSettings({ ...settings, pix_holder: e.target.value })} required={false} /><Field label="URL do provedor de cartão" name="card_payment_url" value={settings.card_payment_url} onChange={(e) => setSettings({ ...settings, card_payment_url: e.target.value })} required={false} placeholder="https://..." /></div>
          <div className="settings-section"><h2>LOJA E ATENDIMENTO</h2><Field label="Faixa de anúncio" name="announcement" value={settings.announcement} onChange={(e) => setSettings({ ...settings, announcement: e.target.value })} /><div className="form-grid two"><Field label="WhatsApp" name="whatsapp" value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} required={false} /><Field label="Instagram" name="instagram" value={settings.instagram} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} required={false} /></div><Field label="E-mail de suporte" name="support_email" value={settings.support_email} onChange={(e) => setSettings({ ...settings, support_email: e.target.value })} required={false} /><Field label="Frete fixo (R$)" name="shipping_flat" value={settings.shipping_flat} onChange={(e) => setSettings({ ...settings, shipping_flat: e.target.value })} /></div>
          <button className="primary-button" onClick={saveSettings}>SALVAR CONFIGURAÇÕES</button>
        </div>}
        {tab === "billing" && <BillingPixPanel />}
      </div>
    </section>
  );
}

function RecentOrders({ orders, update }: { orders: Order[]; update: (id: string, status: string, payment: string) => void }) {
  return <div className="admin-table-wrap"><h2>PEDIDOS</h2>{!orders.length ? <p className="muted">Nenhum pedido registrado.</p> : <div className="admin-table"><div className="table-row head"><span>Pedido</span><span>Data</span><span>Total</span><span>Pagamento</span><span>Status</span></div>{orders.map((order) => <div className="table-row" key={order.id}><span><b>#{order.order_number}</b></span><span>{new Date(`${order.created_at}Z`).toLocaleDateString("pt-BR")}</span><span>{money(order.total_cents)}</span><span><select value={order.payment_status} onChange={(e) => update(order.id, order.status, e.target.value)}><option value="pending">Pendente</option><option value="paid">Pago</option><option value="refunded">Estornado</option><option value="cancelled">Cancelado</option></select></span><span><select value={order.status} onChange={(e) => update(order.id, e.target.value, order.payment_status)}><option value="new">Novo</option><option value="confirmed">Confirmado</option><option value="production">Em produção</option><option value="shipped">Enviado</option><option value="delivered">Entregue</option><option value="cancelled">Cancelado</option></select></span></div>)}</div>}</div>;
}

function Footer({ navigate, settings }: { navigate: (page: Page) => void; settings: Record<string, string> }) {
  return <footer><div className="container footer-grid"><div className="footer-brand"><img src="/images/logo-sgz.png" alt="SGZ" /><p>Vista a energia.<br />Carregue a identidade.</p>{settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer"><span aria-hidden="true">@</span> Instagram</a>}</div><div><b>LOJA</b><button onClick={() => navigate("product")}>Uniforme oficial</button><button onClick={() => navigate("cart")}>Minha sacola</button></div><div><b>ATENDIMENTO</b><button onClick={() => navigate("account")}>Minha conta</button><span>{settings.support_email || "Suporte pela equipe SGZ"}</span></div><div className="footer-secure"><ShieldCheck /><span><b>AMBIENTE SEGURO</b>Dados protegidos e criptografados</span></div></div><div className="footer-bottom container"><span>© 2026 SGZ. Todos os direitos reservados.</span><span>Feito para quem joga junto.</span></div><div className="footer-creator container"><span>Site criado por <a href="https://chamapedido.com.br/" target="_blank" rel="noreferrer">chamapedido.com.br</a></span><a className="footer-creator-logo" href="https://wa.me/5541999527846" target="_blank" rel="noreferrer" aria-label="Falar com a Chama Pedidos no WhatsApp"><img src="/images/chama-pedidos-logo.png" alt="Chama Pedidos" /></a></div></footer>;
}
